import { ArrowDown, ArrowUp, FlaskConical, GitBranch, MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { credentialStatus, hostDefaultCredential, isCredentialTemplate, providerLabel } from '@/lib/gitRouting'
import type { DeploymentGitRouting, GitCredential, GitRoutingRule } from '@/types/gitRouting'
import { CredentialStatusBadge } from './CredentialStatusBadge'

export interface RoutingRulesTableProps {
  rules: GitRoutingRule[]
  isLoading?: boolean
  deployment?: DeploymentGitRouting
  /** The tenant's tokens; undefined when they could not be read, which makes the defaults' status Unknown. */
  credentials?: GitCredential[]
  canEdit: boolean
  canManageTokens: boolean
  isReordering?: boolean
  onAdd: () => void
  onEdit: (rule: GitRoutingRule) => void
  onDelete: (rule: GitRoutingRule) => void
  /** The rules' ids in their new order. */
  onReorder: (ids: string[]) => void
  onTest: (rule: GitRoutingRule) => void
  onSetToken: (credentialName: string) => void
}

/**
 * The tenant's routing rules in the order they are tried (first match wins), with the deployment's own
 * rules and host defaults below them, read-only, so the full precedence is on one screen (GR1, GR5).
 */
export function RoutingRulesTable({
  rules,
  isLoading,
  deployment,
  credentials,
  canEdit,
  canManageTokens,
  isReordering,
  onAdd,
  onEdit,
  onDelete,
  onReorder,
  onTest,
  onSetToken,
}: RoutingRulesTableProps) {
  const ordered = [...rules].sort((a, b) => a.position - b.position)

  const move = (index: number, offset: -1 | 1) => {
    const ids = ordered.map((r) => r.id)
    const target = index + offset
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    onReorder(ids)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Routing rules</CardTitle>
        {canEdit && (
          <Button size="sm" onClick={onAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add rule
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2" data-testid="routing-rules-skeleton">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : ordered.length === 0 ? (
          <EmptyState
            icon={GitBranch}
            title="No routing rules yet"
            description="Repositories use the deployment defaults below. Add a rule when a repository needs a different credential, e.g. a GitHub organisation that needs a token of its own."
            className="py-8"
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>Repository pattern</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Credential</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="w-28" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordered.map((rule, index) => (
                <TableRow key={rule.id} data-testid="routing-rule-row">
                  <TableCell className="font-mono text-muted-foreground">{rule.position}</TableCell>
                  <TableCell className="font-mono text-sm break-all">{rule.urlPattern}</TableCell>
                  <TableCell>{providerLabel(rule.provider)}</TableCell>
                  <TableCell className="font-mono text-sm">{rule.credentialName}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <CredentialStatusBadge status={rule.credentialStatus} />
                      {rule.credentialStatus === 'Missing' && canManageTokens && (
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0"
                          onClick={() => onSetToken(rule.credentialName)}
                        >
                          Set token
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label={`Move rule ${rule.position} up`}
                          disabled={index === 0 || isReordering}
                          onClick={() => move(index, -1)}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label={`Move rule ${rule.position} down`}
                          disabled={index === ordered.length - 1 || isReordering}
                          onClick={() => move(index, 1)}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              aria-label={`Actions for rule ${rule.position}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onEdit(rule)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onTest(rule)}>
                              <FlaskConical className="mr-2 h-4 w-4" />
                              Test with this rule
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onDelete(rule)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <DeploymentDefaults
          deployment={deployment}
          credentials={credentials}
          canManageTokens={canManageTokens}
          onSetToken={onSetToken}
        />
      </CardContent>
    </Card>
  )
}

interface DeploymentDefaultsProps {
  deployment?: DeploymentGitRouting
  credentials?: GitCredential[]
  canManageTokens: boolean
  onSetToken: (credentialName: string) => void
}

/** The operator's rules and host defaults: what applies when no tenant rule matches. */
function DeploymentDefaults({ deployment, credentials, canManageTokens, onSetToken }: DeploymentDefaultsProps) {
  const rows = [
    ...(deployment?.rules ?? []).map((r) => ({
      key: `rule-${r.position}`,
      target: r.urlPattern,
      provider: r.provider,
      credential: r.credentialRef ?? hostDefaultCredential(r.provider),
    })),
    ...(deployment?.hostDefaults ?? []).map((h) => ({
      key: `host-${h.host}`,
      target: h.host,
      provider: h.provider,
      credential: hostDefaultCredential(h.provider),
    })),
  ]

  if (rows.length === 0) {
    return null
  }

  return (
    <div className="border-t pt-4" data-testid="deployment-defaults">
      <p className="mb-2 text-sm text-muted-foreground">
        Deployment defaults (set by your platform operator, read-only)
      </p>
      <div className="space-y-1.5">
        {rows.map((row) => {
          const status = credentialStatus(row.credential, credentials)
          return (
            <div key={row.key} className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,2fr)_auto] items-center gap-3 text-sm">
              <span className="font-mono break-all">{row.target}</span>
              <span>→ {providerLabel(row.provider)}</span>
              <span className="font-mono text-muted-foreground">{row.credential}</span>
              <span className="flex items-center justify-end gap-2">
                {!isCredentialTemplate(row.credential) && <CredentialStatusBadge status={status} />}
                {status === 'Missing' && canManageTokens && (
                  <Button variant="link" size="sm" className="h-auto p-0" onClick={() => onSetToken(row.credential)}>
                    Set token
                  </Button>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
